// ===============================
// UC Suggestions API Communication types
// ===============================

import { useCallback, useContext, useState } from 'react';

import { fg } from '@atlaskit/platform-feature-flags';

import { RequestStatus, UC_SUGGESTIONS_REQUEST_STATUS_KEY } from '../../constants';
import { BFFContext } from '../context';
import { fetchJSON } from '../utils/request';

import { useGenericCache } from './useGenericCache';
import { useURLBuilder } from './useURLBuilder';

export type SuggestionItemProduct =
	| 'jira'
	| 'confluence'
	| 'townsquare'
	| 'mercury'
	| 'teams'
	| 'goal'
	| 'loom';

export interface SuggestionItemResponseBase {
	/**
	 * The ATI of the object type
	 */
	ati: string;
	/**
	 * The name of the object type
	 */
	name: string;
	/**
	 * The product name where the object type belongs to
	 */
	product: SuggestionItemProduct;
	/**
	 * The icon name from @atlaskit/icon/core/{name}
	 */
	icon?: string;
}

export interface IntegrationItemResponse extends SuggestionItemResponseBase {
	type: 'integration';
	actionType: string;
}

export interface IframeItemResponse extends SuggestionItemResponseBase {
	type: 'iframe';
	src: string;
}

export interface LinkItemResponse extends SuggestionItemResponseBase {
	type: 'link';
	target: '_self' | '_blank';
	href: string;
	hostname: string;
	sessionSyncUrl?: string;
	options?: Record<string, string | number | boolean>;
}

export interface PlatformItemResponse extends SuggestionItemResponseBase {
	type: 'platform';
}

export type SuggestionItemResponse =
	| IntegrationItemResponse
	| IframeItemResponse
	| LinkItemResponse
	| PlatformItemResponse;

export interface SuggestionsAPIResponse {
	items: SuggestionItemResponse[];
	isPartial?: boolean;
}

interface UseUCSuggestionsProps {
	cloudId: string;
	orgId: string;
	product: string;
	userId: string;
	locale?: string;
	subProduct?: string;
}

interface UseUCSuggestionsReturn {
	data: SuggestionsAPIResponse | undefined;
	isLoading: boolean;
	hasError: boolean;
	status: RequestStatus | null;
	fetchUCSuggestions: () => Promise<SuggestionsAPIResponse | undefined>;
}

/**
 * Hook for fetching UC suggestions with smart caching
 */
export function useUCSuggestions({
	cloudId,
	orgId,
	product,
	userId,
	locale,
	subProduct,
}: UseUCSuggestionsProps): UseUCSuggestionsReturn {
	const { value, currentLoadingRequests, updateCurrentLoadingRequests } = useContext(BFFContext);
	const [data, setData] = useState<SuggestionsAPIResponse | undefined>(undefined);
	const [hasError, setHasError] = useState(false);

	const { buildUCRecommendationURL } = useURLBuilder({ cloudId });
	const { cachedObject, setCachedObject } = useGenericCache<SuggestionsAPIResponse>({
		cloudId,
		productType: product,
		orgId,
		accountId: userId,
		subProduct,
	});

	const fetchUCSuggestions = useCallback(async (): Promise<SuggestionsAPIResponse | undefined> => {
		// Kill switch: return early if feature flag is disabled
		if (!fg('navx_universal_create_cross_app_creation_enabled')) {
			return undefined;
		}

		// Return cached data if available
		if (cachedObject) {
			setData(cachedObject);
			setHasError(false);
			return cachedObject;
		}

		try {
			const url = buildUCRecommendationURL({ product, orgId, userId, locale, subProduct });
			updateCurrentLoadingRequests(UC_SUGGESTIONS_REQUEST_STATUS_KEY, RequestStatus.LOADING);

			const response = await fetchJSON<SuggestionsAPIResponse>(url, {});

			if (response.status === RequestStatus.ERROR) {
				setHasError(true);
				setData(undefined);
				updateCurrentLoadingRequests(UC_SUGGESTIONS_REQUEST_STATUS_KEY, RequestStatus.ERROR);
				return undefined;
			}

			if (response.status === RequestStatus.COMPLETE && response.data) {
				setData(response.data);
				setHasError(false);

				// Only cache if the data is not partial
				if (!response.data.isPartial) {
					setCachedObject(response.data);
				}

				updateCurrentLoadingRequests(UC_SUGGESTIONS_REQUEST_STATUS_KEY, RequestStatus.COMPLETE);
				return response.data;
			}
		} catch (error) {
			setHasError(true);
			setData(undefined);
			updateCurrentLoadingRequests(UC_SUGGESTIONS_REQUEST_STATUS_KEY, RequestStatus.ERROR);
		}

		return undefined;
	}, [
		cachedObject,
		orgId,
		product,
		userId,
		locale,
		subProduct,
		buildUCRecommendationURL,
		updateCurrentLoadingRequests,
		setCachedObject,
	]);

	// Initialize data from cache on mount
	if (cachedObject && !data) {
		setData(cachedObject);
		setHasError(false);
	}

	const isLoading = currentLoadingRequests?.includes(UC_SUGGESTIONS_REQUEST_STATUS_KEY) ?? false;

	return {
		data,
		isLoading,
		hasError,
		status: value.status,
		fetchUCSuggestions,
	};
}
